/**
 * Coverage Timeline Component
 * Displays a Gantt-style timeline showing data coverage per department
 */

import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { ZoomIn, ZoomOut, Calendar, X, ChevronRight, ChevronDown } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import ExportMenu from '../ExportMenu';

/**
 * Get color for data quality
 */
const getQualityColor = (quality, hasData) => {
  if (!hasData) return 'var(--color-bg-tertiary, #333)';
  switch (quality) {
    case 'high': return 'var(--color-success, #10b981)';
    case 'medium': return 'var(--color-warning, #f59e0b)';
    case 'low': return 'var(--color-orange, #f97316)';
    default: return 'var(--color-error, #ef4444)';
  }
};

/**
 * Format month for display
 */
const formatMonth = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

/**
 * Format currency
 */
const formatCurrency = (val) => {
  const num = parseFloat(val) || 0;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(1)}K`;
  return `$${num.toFixed(2)}`;
};

/**
 * Calculate months between two dates
 */
const monthsBetween = (dateStr1, dateStr2) => {
  const d1 = new Date(dateStr1);
  const d2 = new Date(dateStr2);
  return (d2.getFullYear() - d1.getFullYear()) * 12 + (d2.getMonth() - d1.getMonth());
};

/**
 * Expandable PO Row — shows summary, expands to show line items on click
 */
const PORow = ({ po, departmentId, month }) => {
  const [expanded, setExpanded] = useState(false);
  const [lines, setLines] = useState(null);
  const [loadingLines, setLoadingLines] = useState(false);

  const suppliers = (po.suppliers || []).filter(Boolean);
  const descriptions = (po.descriptions || []).filter(Boolean);
  const components = (po.tech_components || []).filter(Boolean);
  const lineCount = parseInt(po.line_count);
  const summaryDesc = descriptions.length > 0
    ? descriptions[0].slice(0, 80) + (descriptions.length > 1 ? ` (+${descriptions.length - 1} more)` : '')
    : '—';

  const handleToggle = async () => {
    if (lineCount <= 1) return; // Nothing to expand for single-line POs

    if (!expanded && !lines) {
      // Lazy-load line items on first expand
      setLoadingLines(true);
      try {
        const result = await apiClient.get('/api/gap-analysis/transactions/lines', {
          query: { departmentId, month, poNumber: po.po_number }
        });
        setLines(result.lines || []);
      } catch {
        setLines([]);
      } finally {
        setLoadingLines(false);
      }
    }
    setExpanded(prev => !prev);
  };

  return (
    <>
      <tr
        className={`dga-po-row ${lineCount > 1 ? 'expandable' : ''} ${expanded ? 'expanded' : ''}`}
        onClick={handleToggle}
      >
        <td className="dga-po-toggle">
          {lineCount > 1 ? (
            expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
          ) : null}
        </td>
        <td className="dga-drilldown-po">{po.po_number || '—'}</td>
        <td>{suppliers.join(', ') || '—'}</td>
        <td style={{ textAlign: 'right' }}>{lineCount.toLocaleString()}</td>
        <td className="dga-drilldown-amount">{formatCurrency(po.total_amount)}</td>
        <td title={descriptions.join(' | ')}>{summaryDesc}</td>
        <td>{components.length > 0 ? components.join(', ') : '—'}</td>
      </tr>
      {expanded && (
        loadingLines ? (
          <tr className="dga-line-row">
            <td colSpan={7} className="dga-line-loading">Loading line items...</td>
          </tr>
        ) : (lines || []).map((line, i) => (
          <tr key={line.id || i} className="dga-line-row">
            <td></td>
            <td className="dga-line-num">{line.purchase_document_line || i + 1}</td>
            <td colSpan={2} title={line.item_description}>
              {(line.item_description || '—').slice(0, 90)}
              {line.item_description?.length > 90 ? '...' : ''}
            </td>
            <td className="dga-drilldown-amount">{formatCurrency(line.line_total)}</td>
            <td className="dga-line-breakdown">
              {(() => {
                const qty = parseFloat(line.quantity) || 0;
                const price = parseFloat(line.unit_price) || 0;
                if (price > 0 && qty > 0) {
                  return <span>{formatCurrency(price)} &times; {qty.toLocaleString()} {line.purchase_order_uom || ''}</span>;
                }
                if (qty > 0) return <span>{qty.toLocaleString()} {line.purchase_order_uom || 'units'}</span>;
                return <span className="dga-line-na">—</span>;
              })()}
            </td>
            <td>{line.tech_component || '—'}</td>
          </tr>
        ))
      )}
    </>
  );
};

/**
 * Coverage Timeline Component
 */
const CoverageTimeline = ({ data, onDepartmentClick }) => {
  const containerRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownSort, setDrilldownSort] = useState({ key: 'total_amount', dir: 'desc' });
  const [isNarrow, setIsNarrow] = useState(window.innerWidth < 768);
  // Scrape queue: selected stale gap months per department
  const [scrapeQueue, setScrapeQueue] = useState([]); // [{ deptId, deptName, deptCode, acronym, month }]
  const [scrapeStatus, setScrapeStatus] = useState(null); // null | 'confirming' | 'submitting' | 'submitted' | 'error'
  const [scrapeResults, setScrapeResults] = useState([]);

  // Track narrow screen for year-wrap layout
  useEffect(() => {
    const handleResize = () => setIsNarrow(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate timeline bounds — cap at current month (no far-future months)
  const timelineBounds = useMemo(() => {
    if (!data || data.length === 0) return { start: null, end: null, months: [] };

    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;

    const allMonths = data.flatMap(d => d.months.map(m => m.month));
    const uniqueMonths = [...new Set(allMonths)].sort().filter(m => m <= currentMonth);

    return {
      start: uniqueMonths[0],
      end: uniqueMonths[uniqueMonths.length - 1],
      months: uniqueMonths
    };
  }, [data]);

  // Cell width based on zoom
  const cellWidth = useMemo(() => Math.max(24, 40 * zoomLevel), [zoomLevel]);

  // Group months by year for narrow/mobile layout
  const monthsByYear = useMemo(() => {
    if (!timelineBounds.months.length) return [];
    const groups = {};
    timelineBounds.months.forEach(m => {
      const year = m.slice(0, 4);
      if (!groups[year]) groups[year] = [];
      groups[year].push(m);
    });
    return Object.entries(groups).map(([year, months]) => ({ year, months }));
  }, [timelineBounds.months]);

  // Handle zoom
  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(prev + 0.25, 2));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  }, []);

  // Scroll to today marker
  useEffect(() => {
    if (containerRef.current && timelineBounds.months.length > 0) {
      const today = new Date();
      const todayMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
      const todayIndex = timelineBounds.months.findIndex(m => m >= todayMonth);

      if (todayIndex > 0) {
        const scrollPosition = (todayIndex * cellWidth) - (containerRef.current.clientWidth / 2);
        containerRef.current.scrollLeft = Math.max(0, scrollPosition);
      }
    }
  }, [timelineBounds.months, cellWidth]);

  // Handle cell click — fetch transactions for that month
  const handleCellClick = useCallback(async (e, dept, month, monthData) => {
    e.stopPropagation(); // Don't trigger row click

    if (!monthData?.has_data) return; // No data to show

    setDrilldown({
      dept: { id: dept.department_id, name: dept.department_name, acronym: dept.acronym },
      month,
      loading: true,
      items: [],
      total: 0
    });

    try {
      const result = await apiClient.get('/api/gap-analysis/transactions', {
        query: { departmentId: dept.department_id, month }
      });
      setDrilldown(prev => prev ? {
        ...prev,
        loading: false,
        items: result.items || [],
        totalPOs: result.totalPOs || 0,
        totalLines: result.totalLines || 0,
        totalSpend: result.totalSpend || 0
      } : null);
    } catch (err) {
      setDrilldown(prev => prev ? { ...prev, loading: false, error: err.message } : null);
    }
  }, []);

  // Handle stale gap click — toggle in scrape queue
  const handleStaleGapClick = useCallback((e, dept, month) => {
    e.stopPropagation();
    const key = `${dept.department_id}-${month}`;
    setScrapeQueue(prev => {
      const exists = prev.find(item => `${item.deptId}-${item.month}` === key);
      if (exists) {
        return prev.filter(item => `${item.deptId}-${item.month}` !== key);
      }
      return [...prev, {
        deptId: dept.department_id,
        deptName: dept.department_name,
        deptCode: dept.department_code || dept.bu_code || dept.acronym,
        acronym: dept.acronym,
        month
      }];
    });
    setScrapeStatus('confirming');
  }, []);

  const isInScrapeQueue = useCallback((deptId, month) => {
    return scrapeQueue.some(item => item.deptId === deptId && item.month === month);
  }, [scrapeQueue]);

  // Submit scrape jobs for all selected gaps
  const handleSubmitScrapeQueue = useCallback(async () => {
    if (scrapeQueue.length === 0) return;
    setScrapeStatus('submitting');
    const results = [];

    // Group by department to batch date ranges
    const byDept = {};
    scrapeQueue.forEach(item => {
      if (!byDept[item.deptId]) byDept[item.deptId] = { ...item, months: [] };
      byDept[item.deptId].months.push(item.month);
    });

    for (const dept of Object.values(byDept)) {
      // Sort months and create contiguous ranges
      const sorted = dept.months.sort();
      let rangeStart = sorted[0];
      let rangeEnd = sorted[0];

      const submitRange = async (start, end) => {
        const dateFrom = start; // Already YYYY-MM-01
        // End of the last month
        const endDate = new Date(end);
        endDate.setMonth(endDate.getMonth() + 1);
        endDate.setDate(0); // Last day of month
        const dateTo = endDate.toISOString().slice(0, 10);

        // Scrape BOTH IT Goods and IT Services to fill any gaps
        const acquisitionTypes = ['IT Goods', 'IT Services'];
        for (const acquisitionType of acquisitionTypes) {
          try {
            const result = await apiClient.post('/api/scraper/scrape/scprs', {
              dateFrom,
              dateTo,
              departmentCode: dept.deptCode,
              acquisitionType
            });
            results.push({ dept: dept.acronym, dateFrom, dateTo, acquisitionType, status: 'queued', jobId: result?.job?.id });
          } catch (err) {
            results.push({ dept: dept.acronym, dateFrom, dateTo, acquisitionType, status: 'error', error: err.message });
          }
        }
      };

      for (let i = 1; i < sorted.length; i++) {
        // Check if months are contiguous
        const prevEnd = new Date(rangeEnd);
        prevEnd.setMonth(prevEnd.getMonth() + 1);
        const expected = `${prevEnd.getFullYear()}-${String(prevEnd.getMonth() + 1).padStart(2, '0')}-01`;

        if (sorted[i] === expected) {
          rangeEnd = sorted[i]; // Extend range
        } else {
          await submitRange(rangeStart, rangeEnd); // Submit current range
          rangeStart = sorted[i];
          rangeEnd = sorted[i];
        }
      }
      await submitRange(rangeStart, rangeEnd); // Submit final range
    }

    setScrapeResults(results);
    setScrapeStatus('submitted');
  }, [scrapeQueue]);

  const handleClearScrapeQueue = useCallback(() => {
    setScrapeQueue([]);
    setScrapeStatus(null);
    setScrapeResults([]);
  }, []);

  if (!data || data.length === 0) {
    return (
      <div className="dga-timeline-empty">
        <Calendar size={48} />
        <h3>No Timeline Data</h3>
        <p>Select departments to view their data coverage timeline</p>
      </div>
    );
  }

  const todayMonth = (() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  })();

  // Find last month with data per department (for staleness indicator)
  const getLastDataMonth = (dept) => {
    if (!dept.months) return null;
    const withData = dept.months.filter(m => m.has_data).sort((a, b) => b.month.localeCompare(a.month));
    return withData[0]?.month || null;
  };

  return (
    <div className="dga-timeline">
      {/* Header: dept info + legend */}
      <div className="dga-timeline-controls">
        <div className="dga-grid-dept-info">
          {data.map(dept => {
            const lastDataMonth = getLastDataMonth(dept);
            const staleMonths = lastDataMonth ? monthsBetween(lastDataMonth, todayMonth) : null;
            return (
              <div key={dept.department_id} className="dga-grid-dept-badge">
                <span className="dga-timeline-acronym">{dept.acronym || '—'}</span>
                <span className="dga-grid-dept-name">{dept.department_name}</span>
                {staleMonths > 0 && <span className="dga-stale-badge">{staleMonths}mo behind</span>}
              </div>
            );
          })}
        </div>
        <div className="dga-timeline-legend">
          <span className="dga-legend-item">
            <span className="dga-legend-dot" style={{ background: 'var(--color-success)' }} />
            High Quality
          </span>
          <span className="dga-legend-item">
            <span className="dga-legend-dot" style={{ background: 'var(--color-warning)' }} />
            Medium
          </span>
          <span className="dga-legend-item">
            <span className="dga-legend-dot" style={{ background: 'var(--color-orange)' }} />
            Low
          </span>
          <span className="dga-legend-item">
            <span className="dga-legend-dot" style={{ background: 'var(--color-bg-tertiary)' }} />
            No Data
          </span>
          <span className="dga-legend-item">
            <span className="dga-legend-dot dga-legend-dot-striped" />
            Stale Gap
          </span>
          <span className="dga-legend-item">
            <span className="dga-legend-dot" style={{ background: 'transparent', border: '2px solid #38bdf8', boxSizing: 'border-box' }} />
            Has Detail Data
          </span>
          <span className="dga-legend-sep">|</span>
          <span className="dga-legend-item">
            <span className="dga-legend-dot" style={{ background: '#10b981' }} />
            IT Goods
          </span>
          <span className="dga-legend-item">
            <span className="dga-legend-dot" style={{ background: '#8b5cf6' }} />
            IT Services
          </span>
          <span className="dga-legend-item">
            <span className="dga-legend-dot dga-legend-dot-half" />
            Goods Only (no Services)
          </span>
        </div>
        <div className="dga-timeline-zoom">
          <button className="dga-btn-icon" onClick={handleZoomOut} title="Zoom out">
            <ZoomOut size={16} />
          </button>
          <span className="dga-zoom-level">{Math.round(zoomLevel * 100)}%</span>
          <button className="dga-btn-icon" onClick={handleZoomIn} title="Zoom in">
            <ZoomIn size={16} />
          </button>
        </div>
      </div>

      {/* Year-over-Year Grid: rows = years, cols = 12 months */}
      {(() => {
        const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const MN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return (
          <div className="dga-grid">
            {/* Month column headers */}
            <div className="dga-grid-header">
              <div className="dga-grid-year-col" />
              {MN.map(m => <div key={m} className="dga-grid-month-col">{m}</div>)}
            </div>

            {/* One row per year */}
            {data.flatMap(dept => {
              const lastDataMonth = getLastDataMonth(dept);
              return monthsByYear.map(({ year }) => {
                const isCurrentYear = year === String(new Date().getFullYear());
                return (
                  <div key={`${dept.department_id}-${year}`} className={`dga-grid-row ${isCurrentYear ? 'current-year' : ''}`}>
                    <div className="dga-grid-year-col">{year}</div>
                    {MN.map((_, mi) => {
                      const mk = `${year}-${String(mi + 1).padStart(2, '0')}-01`;
                      const md = dept.months.find(m => m.month === mk);
                      const has = md?.has_data ?? false;
                      const q = md?.data_quality ?? 'no_data';
                      const isToday = mk === todayMonth;
                      const isFuture = mk > todayMonth;
                      const inRange = timelineBounds.months.includes(mk);
                      const isStale = !has && !isFuture && lastDataMonth && mk > lastDataMonth && mk <= todayMonth;
                      const isQ = isInScrapeQueue(dept.department_id, mk);

                      if (!inRange && !isFuture) return <div key={mk} className="dga-grid-cell out-of-range" />;

                      const detailPct = md?.detail_pct || 0;
                      const hasDetail = md?.detail_total_pos > 0;
                      const detailTitle = hasDetail
                        ? ` | Detail: ${md.detail_extracted}/${md.detail_total_pos} POs (${detailPct}%)`
                        : '';

                      // Acquisition type breakdown
                      const hasGoods = md?.has_it_goods;
                      const hasServices = md?.has_it_services;
                      const acqTypes = md?.acq_types || {};
                      const goodsPOs = acqTypes['IT Goods']?.pos || 0;
                      const servicesPOs = acqTypes['IT Services']?.pos || 0;
                      const acqTitle = has
                        ? ` | Goods: ${goodsPOs} POs, Services: ${servicesPOs} POs`
                        : '';

                      // Color: green if both, purple tint if services only, default green for goods only
                      const cellBg = isFuture || !inRange ? 'transparent'
                        : !has ? getQualityColor(q, has)
                        : hasGoods && hasServices ? 'linear-gradient(135deg, #10b981 60%, #8b5cf6 100%)'
                        : hasServices && !hasGoods ? '#8b5cf6'
                        : getQualityColor(q, has);
                      const useGradient = has && hasGoods && hasServices;

                      return (
                        <div
                          key={mk}
                          className={`dga-grid-cell ${has ? 'has-data' : ''} ${isStale ? 'stale-gap' : ''} ${isFuture ? 'future' : ''} ${isToday ? 'today' : ''} ${isQ ? 'queued' : ''} ${has || isStale ? 'clickable' : ''} ${has && !hasServices && hasGoods ? 'goods-only' : ''}`}
                          style={useGradient
                            ? { background: cellBg }
                            : { backgroundColor: cellBg }
                          }
                          onClick={(e) => {
                            if (has) handleCellClick(e, dept, mk, md);
                            else if (isStale) handleStaleGapClick(e, dept, mk);
                          }}
                          title={!inRange ? '' : has ? `${formatMonth(mk)}: ${md.record_count.toLocaleString()} POs, ${formatCurrency(md.total_spend)}${acqTitle}${detailTitle}` : isStale ? (isQ ? 'Queued — click to remove' : 'Gap — click to scrape') : ''}
                        >
                          {has && md.record_count > 0 && (
                            <span className="dga-grid-cell-ct">{md.record_count > 999 ? `${Math.round(md.record_count / 1000)}k` : md.record_count}</span>
                          )}
                          {has && !hasServices && hasGoods && (
                            <div className="dga-grid-cell-no-svc" title="No IT Services data" />
                          )}
                          {hasDetail && detailPct < 100 && (
                            <div className="dga-grid-cell-detail" style={{ width: `${detailPct}%` }} />
                          )}
                          {hasDetail && detailPct >= 100 && (
                            <div className="dga-grid-cell-detail-complete" />
                          )}
                          {isToday && <div className="dga-grid-now" />}
                        </div>
                      );
                    })}
                  </div>
                );
              });
            })}
          </div>
        );
      })()}

      {/* Scrape Queue Panel */}
      {scrapeQueue.length > 0 && (
        <div className="dga-scrape-panel">
          <div className="dga-scrape-panel-header">
            <div>
              <strong>Scrape Queue</strong>
              <span className="dga-scrape-count">{scrapeQueue.length} month{scrapeQueue.length !== 1 ? 's' : ''} selected</span>
            </div>
            <button className="dga-btn-icon" onClick={handleClearScrapeQueue} title="Clear queue">
              <X size={16} />
            </button>
          </div>

          <div className="dga-scrape-months">
            {scrapeQueue.map(item => (
              <span
                key={`${item.deptId}-${item.month}`}
                className="dga-scrape-tag"
                onClick={() => handleStaleGapClick({ stopPropagation: () => {} }, { department_id: item.deptId, department_name: item.deptName, department_code: item.deptCode, acronym: item.acronym }, item.month)}
              >
                {item.acronym} {formatMonth(item.month)}
                <X size={10} />
              </span>
            ))}
          </div>

          <div className="dga-scrape-actions">
            {scrapeStatus === 'submitted' ? (
              <div className="dga-scrape-results">
                {scrapeResults.map((r, i) => (
                  <span key={i} className={`dga-scrape-result ${r.status}`}>
                    {r.dept} {r.dateFrom.slice(0, 7)} {r.acquisitionType}: {r.status === 'queued' ? 'Queued' : r.error || 'Error'}
                  </span>
                ))}
                <button className="dga-btn dga-btn-ghost" onClick={handleClearScrapeQueue}>
                  Done
                </button>
              </div>
            ) : (
              <>
                <span className="dga-scrape-info">
                  IT Goods + Services scrape for {[...new Set(scrapeQueue.map(q => q.acronym))].join(', ')}
                </span>
                <button
                  className="dga-btn dga-btn-primary"
                  onClick={handleSubmitScrapeQueue}
                  disabled={scrapeStatus === 'submitting'}
                >
                  {scrapeStatus === 'submitting' ? 'Submitting...' : `Scrape ${scrapeQueue.length} Gap${scrapeQueue.length !== 1 ? 's' : ''}`}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Drilldown Modal — POs for a specific month */}
      {drilldown && (
        <div className="dga-drilldown-overlay" onClick={() => setDrilldown(null)}>
          <div className="dga-drilldown-modal" onClick={e => e.stopPropagation()}>
            <div className="dga-drilldown-header">
              <div>
                <h3>
                  {drilldown.dept.acronym} — {formatMonth(drilldown.month)}
                </h3>
                <p>
                  {drilldown.dept.name} &middot;{' '}
                  {drilldown.totalPOs?.toLocaleString() || 0} POs &middot;{' '}
                  {drilldown.totalLines?.toLocaleString() || 0} line items &middot;{' '}
                  {formatCurrency(drilldown.totalSpend || 0)}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ExportMenu
                  data={drilldown.items || []}
                  columns={[
                    { key: 'po_number', label: 'PO #' },
                    { key: 'suppliers', label: 'Supplier' },
                    { key: 'line_count', label: 'Lines' },
                    { key: 'total_amount', label: 'Total' },
                    { key: 'descriptions', label: 'Description' },
                    { key: 'tech_components', label: 'Components' }
                  ]}
                  filename={`${drilldown.dept.acronym}-${drilldown.month}`}
                  size="sm"
                  disabled={drilldown.loading || !drilldown.items?.length}
                />
                <button className="dga-btn-icon" onClick={() => setDrilldown(null)}>
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="dga-drilldown-body">
              {drilldown.loading ? (
                <div className="dga-drilldown-loading">Loading transactions...</div>
              ) : drilldown.error ? (
                <div className="dga-drilldown-error">{drilldown.error}</div>
              ) : drilldown.items.length === 0 ? (
                <div className="dga-drilldown-empty">No purchase orders found</div>
              ) : (() => {
                // Sort items
                const sortedItems = [...drilldown.items].sort((a, b) => {
                  let av, bv;
                  switch (drilldownSort.key) {
                    case 'total_amount':
                      av = parseFloat(a.total_amount) || 0;
                      bv = parseFloat(b.total_amount) || 0;
                      break;
                    case 'line_count':
                      av = parseInt(a.line_count) || 0;
                      bv = parseInt(b.line_count) || 0;
                      break;
                    case 'po_number':
                      av = a.po_number || '';
                      bv = b.po_number || '';
                      return drilldownSort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
                    case 'supplier':
                      av = (a.suppliers || [])[0] || '';
                      bv = (b.suppliers || [])[0] || '';
                      return drilldownSort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
                    default:
                      av = parseFloat(a.total_amount) || 0;
                      bv = parseFloat(b.total_amount) || 0;
                  }
                  return drilldownSort.dir === 'asc' ? av - bv : bv - av;
                });

                const SortHeader = ({ label, sortKey, align }) => (
                  <th
                    className="dga-sortable-th"
                    style={{ textAlign: align || 'left', cursor: 'pointer' }}
                    onClick={() => setDrilldownSort(prev => ({
                      key: sortKey,
                      dir: prev.key === sortKey && prev.dir === 'desc' ? 'asc' : 'desc'
                    }))}
                  >
                    {label}
                    {drilldownSort.key === sortKey && (
                      <span className="dga-sort-arrow">{drilldownSort.dir === 'asc' ? ' ↑' : ' ↓'}</span>
                    )}
                  </th>
                );

                return (
                  <table className="dga-drilldown-table">
                    <thead>
                      <tr>
                        <th style={{ width: 28 }}></th>
                        <SortHeader label="PO #" sortKey="po_number" />
                        <SortHeader label="Supplier" sortKey="supplier" />
                        <SortHeader label="Lines" sortKey="line_count" align="right" />
                        <SortHeader label="Total" sortKey="total_amount" align="right" />
                        <th>Description</th>
                        <th>Components</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedItems.map((po, idx) => (
                        <PORow
                          key={po.po_number || idx}
                          po={po}
                          departmentId={drilldown.dept.id}
                          month={drilldown.month}
                        />
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .dga-timeline {
          background: var(--color-surface, #1a1a1a);
          border: 1px solid var(--color-border, #333);
          border-radius: 12px;
        }

        .dga-timeline-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          border-bottom: 1px solid var(--color-border, #333);
        }

        .dga-timeline-legend {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .dga-legend-dot-striped {
          background: repeating-linear-gradient(
            45deg,
            var(--color-error, #ef4444),
            var(--color-error, #ef4444) 2px,
            transparent 2px,
            transparent 4px
          ) !important;
        }

        .dga-timeline-zoom {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .dga-zoom-level {
          font-size: 0.75rem;
          color: var(--color-text-secondary, #888);
          min-width: 40px;
          text-align: center;
        }

        .dga-timeline-wrapper {
          overflow-x: auto;
          overflow-y: auto;
          max-height: 500px;
          -webkit-overflow-scrolling: touch;
        }

        .dga-timeline-grid {
          display: flex;
          flex-direction: column;
        }

        .dga-timeline-header {
          display: flex;
          position: sticky;
          top: 0;
          z-index: 2;
          background: var(--color-bg-tertiary, #252525);
          border-bottom: 1px solid var(--color-border, #333);
        }

        .dga-timeline-dept-header {
          width: 200px;
          min-width: 200px;
          padding: 10px 16px;
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-text-secondary, #888);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          position: sticky;
          left: 0;
          background: var(--color-bg-tertiary, #252525);
          z-index: 3;
        }

        .dga-timeline-months {
          display: flex;
        }

        .dga-timeline-month {
          padding: 10px 4px;
          font-size: 0.6875rem;
          color: var(--color-text-tertiary, #666);
          text-align: center;
          border-left: 1px solid var(--color-border, #333);
          position: relative;
        }

        .dga-timeline-month.today {
          color: var(--color-primary, #6366f1);
          font-weight: 700;
          background: rgba(99, 102, 241, 0.08);
        }

        .dga-timeline-month.future {
          opacity: 0.4;
        }

        .dga-timeline-month.year-start {
          border-left-width: 2px;
          border-left-color: var(--color-text-tertiary, #666);
        }

        .dga-today-marker {
          position: absolute;
          bottom: -1px;
          left: 50%;
          transform: translateX(-50%);
          width: 2px;
          height: 2000px;
          background: var(--color-primary, #6366f1);
          opacity: 0.6;
          pointer-events: none;
          z-index: 5;
        }

        .dga-today-label {
          position: absolute;
          top: -18px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 9px;
          font-weight: 800;
          color: var(--color-primary, #6366f1);
          background: var(--color-bg-tertiary, #252525);
          padding: 1px 5px;
          border-radius: 3px;
          letter-spacing: 0.1em;
          white-space: nowrap;
        }

        .dga-timeline-row {
          display: flex;
          border-bottom: 1px solid var(--color-border, #333);
          cursor: pointer;
          transition: background 0.15s;
        }

        .dga-timeline-row:hover {
          background: var(--color-bg-hover, #252525);
        }

        .dga-timeline-dept {
          width: 200px;
          min-width: 200px;
          padding: 8px 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          position: sticky;
          left: 0;
          background: var(--color-bg-secondary, #1a1a1a);
          z-index: 1;
        }

        .dga-timeline-row:hover .dga-timeline-dept {
          background: var(--color-bg-hover, #252525);
        }

        .dga-timeline-acronym {
          font-size: 0.6875rem;
          font-weight: 600;
          color: var(--color-primary, #6366f1);
          background: rgba(99, 102, 241, 0.1);
          padding: 2px 6px;
          border-radius: 4px;
          min-width: 36px;
          text-align: center;
        }

        .dga-timeline-name {
          font-size: 0.8125rem;
          color: var(--color-text-primary, #fff);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100px;
        }

        .dga-stale-badge {
          font-size: 0.625rem;
          font-weight: 700;
          color: #ef4444;
          background: rgba(239, 68, 68, 0.1);
          padding: 1px 5px;
          border-radius: 4px;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .dga-timeline-cells {
          display: flex;
        }

        .dga-timeline-cell {
          height: 36px;
          border-left: 1px solid rgba(0, 0, 0, 0.2);
          position: relative;
          transition: opacity 0.15s;
        }

        .dga-timeline-cell.clickable {
          cursor: pointer;
        }

        .dga-timeline-cell.clickable:hover {
          opacity: 0.8;
          outline: 2px solid var(--color-primary, #6366f1);
          outline-offset: -2px;
          z-index: 1;
        }

        .dga-timeline-cell.hovered {
          opacity: 0.8;
          outline: 2px solid var(--color-primary, #6366f1);
          outline-offset: -2px;
          z-index: 1;
        }

        .dga-timeline-cell.stale-gap {
          background: repeating-linear-gradient(
            45deg,
            rgba(239, 68, 68, 0.15),
            rgba(239, 68, 68, 0.15) 3px,
            transparent 3px,
            transparent 6px
          ) !important;
        }

        .dga-timeline-cell.future {
          background: transparent !important;
          opacity: 0.3;
        }

        .dga-cell-tooltip {
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: var(--color-bg-primary, #0a0a0a);
          border: 1px solid var(--color-border, #333);
          border-radius: 6px;
          padding: 8px 12px;
          font-size: 0.75rem;
          white-space: nowrap;
          z-index: 10;
          display: flex;
          flex-direction: column;
          gap: 2px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .dga-cell-tooltip strong {
          color: var(--color-text-primary, #fff);
        }

        .dga-cell-tooltip span {
          color: var(--color-text-secondary, #888);
        }

        .dga-cell-tooltip-hint {
          color: var(--color-primary, #6366f1) !important;
          font-size: 0.6875rem;
          margin-top: 2px;
        }

        /* =============================================
           DRILLDOWN MODAL
           ============================================= */

        .dga-drilldown-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }

        .dga-drilldown-modal {
          background: var(--color-surface, #18181b);
          border: 1px solid var(--color-border, #333);
          border-radius: 16px;
          width: 100%;
          max-width: 900px;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
        }

        .dga-drilldown-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid var(--color-border, #333);
        }

        .dga-drilldown-header h3 {
          font-size: 18px;
          font-weight: 700;
          color: var(--color-text, #fafafa);
          margin: 0;
        }

        .dga-drilldown-header p {
          font-size: 13px;
          color: var(--color-text-muted, #a1a1aa);
          margin: 4px 0 0;
        }

        .dga-drilldown-body {
          flex: 1;
          overflow-y: auto;
          padding: 0;
        }

        .dga-drilldown-loading,
        .dga-drilldown-error,
        .dga-drilldown-empty {
          padding: 48px 24px;
          text-align: center;
          color: var(--color-text-muted, #a1a1aa);
          font-size: 14px;
        }

        .dga-drilldown-error {
          color: #ef4444;
        }

        .dga-drilldown-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .dga-drilldown-table th {
          position: sticky;
          top: 0;
          background: var(--color-bg-tertiary, #252525);
          padding: 10px 16px;
          text-align: left;
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--color-text-muted, #a1a1aa);
          border-bottom: 1px solid var(--color-border, #333);
        }

        .dga-drilldown-table td {
          padding: 10px 16px;
          color: var(--color-text, #fafafa);
          border-bottom: 1px solid var(--color-border, rgba(255,255,255,0.04));
          max-width: 250px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .dga-drilldown-table tr:hover td {
          background: var(--color-surface-hover, rgba(255,255,255,0.03));
        }

        .dga-drilldown-po {
          font-family: 'JetBrains Mono', 'SF Mono', monospace;
          font-size: 12px;
          color: var(--color-primary, #6366f1);
          white-space: nowrap;
        }

        .dga-drilldown-amount {
          font-weight: 600;
          font-variant-numeric: tabular-nums;
          text-align: right;
          white-space: nowrap;
        }

        /* Expandable PO rows */
        .dga-po-row.expandable {
          cursor: pointer;
        }

        .dga-po-row.expandable:hover td {
          background: var(--color-surface-hover, rgba(255,255,255,0.05));
        }

        .dga-po-row.expanded td {
          background: rgba(99, 102, 241, 0.04);
          border-bottom-color: transparent;
        }

        .dga-po-toggle {
          width: 28px;
          text-align: center;
          color: var(--color-text-muted, #a1a1aa);
          padding: 10px 6px !important;
        }

        .dga-po-row.expandable .dga-po-toggle {
          color: var(--color-primary, #6366f1);
        }

        /* Line item sub-rows */
        .dga-line-row td {
          padding: 7px 16px !important;
          font-size: 12px;
          color: var(--color-text-muted, #a1a1aa);
          background: rgba(99, 102, 241, 0.02);
          border-bottom: 1px solid rgba(255, 255, 255, 0.02);
        }

        .dga-line-row:last-of-type td {
          border-bottom: 1px solid var(--color-border, rgba(255,255,255,0.06));
        }

        .dga-line-num {
          font-family: 'JetBrains Mono', 'SF Mono', monospace;
          font-size: 11px;
          color: var(--color-text-muted, #71717a);
        }

        .dga-line-loading {
          text-align: center;
          color: var(--color-text-muted, #a1a1aa);
          padding: 16px !important;
        }

        .dga-line-breakdown {
          font-size: 12px;
          color: var(--color-text-muted, #a1a1aa);
          white-space: nowrap;
          font-variant-numeric: tabular-nums;
        }

        .dga-line-na {
          opacity: 0.4;
        }

        .dga-timeline-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
          color: var(--color-text-secondary, #888);
        }

        .dga-timeline-empty svg {
          margin-bottom: 16px;
          opacity: 0.5;
        }

        .dga-timeline-empty h3 {
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--color-text-primary, #fff);
          margin: 0 0 8px;
        }

        .dga-timeline-empty p {
          font-size: 0.875rem;
          color: var(--color-text-tertiary, #666);
          margin: 0;
        }

        /* Sortable table headers */
        /* Queued cell state */
        .dga-timeline-cell.queued,
        .dga-year-cell.queued {
          outline: 2px solid #f59e0b !important;
          outline-offset: -2px;
          background: rgba(245, 158, 11, 0.25) !important;
          z-index: 1;
        }

        .dga-timeline-cell.stale-gap.clickable {
          cursor: pointer;
        }

        .dga-timeline-cell.stale-gap.clickable:hover {
          outline: 2px solid #f59e0b;
          outline-offset: -2px;
          z-index: 1;
        }

        /* Scrape Queue Panel */
        .dga-scrape-panel {
          border-top: 2px solid #f59e0b;
          background: var(--color-bg-secondary, #1a1a1a);
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .dga-scrape-panel-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .dga-scrape-panel-header strong {
          font-size: 14px;
          color: var(--color-text-primary, #fff);
        }

        .dga-scrape-count {
          margin-left: 8px;
          font-size: 12px;
          color: #f59e0b;
          font-weight: 600;
        }

        .dga-scrape-months {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .dga-scrape-tag {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          background: rgba(245, 158, 11, 0.15);
          border: 1px solid rgba(245, 158, 11, 0.3);
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
          color: #f59e0b;
          cursor: pointer;
          transition: all 0.15s;
        }

        .dga-scrape-tag:hover {
          background: rgba(245, 158, 11, 0.25);
          border-color: #f59e0b;
        }

        .dga-scrape-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        .dga-scrape-info {
          font-size: 13px;
          color: var(--color-text-secondary, #888);
        }

        .dga-btn-primary {
          padding: 8px 20px;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: #fff;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
          white-space: nowrap;
        }

        .dga-btn-primary:hover {
          filter: brightness(1.1);
          transform: translateY(-1px);
        }

        .dga-btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .dga-scrape-results {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
        }

        .dga-scrape-result {
          font-size: 12px;
          padding: 4px 10px;
          border-radius: 6px;
          font-weight: 500;
        }

        .dga-scrape-result.queued {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }

        .dga-scrape-result.error {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .dga-sortable-th {
          user-select: none;
          transition: color 0.15s;
        }

        .dga-sortable-th:hover {
          color: var(--color-primary, #6366f1);
        }

        .dga-sort-arrow {
          font-size: 10px;
          opacity: 0.7;
        }

        /* =============================================
           YEAR-WRAP MOBILE LAYOUT
           ============================================= */

        .dga-timeline-years {
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .dga-timeline-year-dept {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .dga-timeline-dept-label {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 0;
          font-size: 0.8125rem;
          color: var(--color-text-primary, #fff);
          font-weight: 500;
        }

        .dga-timeline-year-row {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .dga-timeline-year-label {
          font-size: 0.6875rem;
          font-weight: 700;
          color: var(--color-text-tertiary, #666);
          min-width: 36px;
          text-align: right;
        }

        .dga-timeline-year-cells {
          display: flex;
          flex-wrap: wrap;
          gap: 2px;
          flex: 1;
        }

        .dga-year-cell {
          flex: 1;
          min-width: 28px;
          height: 28px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          transition: opacity 0.15s;
        }

        .dga-year-cell.clickable {
          cursor: pointer;
        }

        .dga-year-cell.clickable:hover {
          opacity: 0.8;
          outline: 2px solid var(--color-primary, #6366f1);
          outline-offset: -1px;
        }

        .dga-year-cell.today {
          outline: 2px solid var(--color-primary, #6366f1);
          outline-offset: -1px;
        }

        .dga-year-cell.stale-gap {
          background: repeating-linear-gradient(
            45deg,
            rgba(239, 68, 68, 0.15),
            rgba(239, 68, 68, 0.15) 3px,
            transparent 3px,
            transparent 6px
          ) !important;
        }

        .dga-year-cell.future {
          opacity: 0.3;
        }

        .dga-year-cell-label {
          font-size: 8px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.7);
          pointer-events: none;
        }

        .dga-year-cell:not(.has-data) .dga-year-cell-label {
          color: var(--color-text-tertiary, #555);
        }

        @media (max-width: 768px) {
          .dga-drilldown-modal {
            max-height: 90vh;
          }
          .dga-drilldown-table td,
          .dga-drilldown-table th {
            padding: 8px 10px;
            font-size: 12px;
          }
        }

        /* =============================================
           YEAR-OVER-YEAR GRID
           ============================================= */

        .dga-grid-dept-info {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .dga-grid-dept-badge {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .dga-grid-dept-name {
          font-size: 14px;
          font-weight: 500;
          color: var(--color-text, #fff);
        }

        .dga-grid {
          padding: 4px 16px 16px;
        }

        .dga-grid-header {
          display: grid;
          grid-template-columns: 56px repeat(12, 1fr);
          gap: 3px;
          margin-bottom: 3px;
        }

        .dga-grid-year-col {
          font-size: 12px;
          font-weight: 700;
          color: var(--color-text-muted, #888);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px 0;
        }

        .dga-grid-month-col {
          font-size: 11px;
          font-weight: 600;
          color: var(--color-text-muted, #666);
          text-align: center;
          padding: 6px 0;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .dga-grid-row {
          display: grid;
          grid-template-columns: 56px repeat(12, 1fr);
          gap: 3px;
          margin-bottom: 3px;
        }

        .dga-grid-row.current-year {
          background: rgba(99, 102, 241, 0.04);
          border-radius: 6px;
        }

        .dga-grid-cell {
          height: 40px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          transition: all 0.15s;
          font-size: 11px;
        }

        .dga-grid-cell.out-of-range {
          background: transparent;
        }

        .dga-grid-cell.clickable {
          cursor: pointer;
        }

        .dga-grid-cell.clickable:hover {
          opacity: 0.8;
          outline: 2px solid var(--color-primary, #6366f1);
          outline-offset: -1px;
          z-index: 1;
        }

        .dga-grid-cell.today {
          outline: 2px solid var(--color-primary, #6366f1);
          outline-offset: -1px;
          z-index: 1;
        }

        .dga-grid-cell.stale-gap {
          background: repeating-linear-gradient(
            45deg,
            rgba(239, 68, 68, 0.15),
            rgba(239, 68, 68, 0.15) 3px,
            transparent 3px,
            transparent 6px
          ) !important;
        }

        .dga-grid-cell.stale-gap.clickable:hover {
          outline-color: #f59e0b;
        }

        .dga-grid-cell.queued {
          outline: 2px solid #f59e0b !important;
          outline-offset: -1px;
          background: rgba(245, 158, 11, 0.25) !important;
          z-index: 1;
        }

        .dga-grid-cell.future {
          opacity: 0.2;
        }

        .dga-grid-cell-ct {
          font-size: 10px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.85);
          pointer-events: none;
        }

        .dga-grid-now {
          position: absolute;
          bottom: 2px;
          left: 50%;
          transform: translateX(-50%);
          width: 6px;
          height: 6px;
          background: var(--color-primary, #6366f1);
          border-radius: 50%;
          pointer-events: none;
        }

        .dga-grid-cell-no-svc {
          position: absolute;
          top: 2px;
          right: 2px;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #f59e0b;
          pointer-events: none;
          opacity: 0.8;
        }

        .dga-grid-cell.goods-only {
          box-shadow: inset 0 0 0 1px rgba(245, 158, 11, 0.3);
        }

        .dga-legend-sep {
          color: var(--color-text-tertiary, #555);
          font-size: 12px;
          margin: 0 4px;
        }

        .dga-legend-dot-half {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: linear-gradient(135deg, #10b981 50%, #f59e0b 50%);
        }

        .dga-grid-cell-detail {
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          background: #38bdf8;
          border-radius: 0 0 0 6px;
          pointer-events: none;
          min-width: 3px;
        }

        .dga-grid-cell-detail-complete {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: #38bdf8;
          border-radius: 0 0 6px 6px;
          pointer-events: none;
        }

        @media (max-width: 480px) {
          .dga-grid-cell {
            height: 32px;
          }
          .dga-grid-header {
            grid-template-columns: 40px repeat(12, 1fr);
          }
          .dga-grid-row {
            grid-template-columns: 40px repeat(12, 1fr);
          }
          .dga-grid-year-col {
            font-size: 10px;
          }
          .dga-grid-month-col {
            font-size: 9px;
          }
        }
      `}</style>
    </div>
  );
};

export default CoverageTimeline;
